/** Wait between attempts, releasing the timer immediately when canceled. */
export function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  signal?.throwIfAborted();
  if (ms <= 0) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    };
    const onAbort = () => {
      cleanup();
      reject(signal?.reason);
    };
    const timer = setTimeout(() => {
      cleanup();
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

interface RetryOptions {
  maxAttempts: number;
  baseDelayMs: number;
  /** Override the base exponential schedule while retaining shared jitter/cancellation. */
  delayMs?: (failedAttempt: number) => number;
  shouldRetry: (error: unknown) => boolean;
  signal?: AbortSignal;
  jitterRatio?: number;
}

/** Retry transient failures with exponential delays; attempts are numbered from 1. */
export async function retryWithBackoff<T>(
  operation: (attempt: number) => Promise<T>,
  {
    maxAttempts,
    baseDelayMs,
    delayMs,
    shouldRetry,
    signal,
    jitterRatio = 0.1,
  }: RetryOptions
): Promise<T> {
  for (let attempt = 1; ; attempt += 1) {
    signal?.throwIfAborted();
    try {
      return await operation(attempt);
    } catch (error) {
      if (attempt >= maxAttempts || !shouldRetry(error)) throw error;
      const delay = delayMs?.(attempt) ?? baseDelayMs * 2 ** (attempt - 1);
      await sleep(
        delay * (1 - jitterRatio + Math.random() * 2 * jitterRatio),
        signal
      );
    }
  }
}
